// QuickBooks customer ↔ billing record matching (partner / chain / hotel / vendor). ONE copy, used by the QuickBooks
// Customers page and its Link customers wizard, so both suggest exactly the same pairs — in either direction.
//
// A suggestion is a name match after dropping the words every hospitality company shares ("Hotels", "Group",
// "Ltd" …), or the same billing email on both sides. It is only ever offered — similar names can belong to
// unrelated companies.

const NOISE = /\b(the|hotels?|resorts?|group|collection|hospitality|ltd|limited|llc|inc|gmbh|sa|plc|and|&)\b/g;

export const norm = (s) => String(s || '').toLowerCase().replace(NOISE, ' ').replace(/[^a-z0-9]/g, '');
export const keyOf = (type, id) => (type && id ? `${type}:${id}` : '');
const normEmail = (s) => String(s || '').trim().toLowerCase();

// A customer is linked to EXACTLY ONE of these (acct_customer_links, V566/V567): choosing another replaces it.
const TYPE_RANK = { partner: 0, account: 1, hotel: 2, vendor: 3 };

const where = (r) => [r.billingCity, r.billingCountry].filter(Boolean).join(', ');

/**
 * Everything a customer can link to, from GET /finance/customer-links: partners, chains, hotels, vendors.
 * `billingRecord` marks a record that pays (billed to itself, or a vendor / partner with billing details) — the
 * records the agent proposes QuickBooks customers for, and the ones the wizard's "Billing records" view walks.
 */
export const buildTargets = (hub) => [
    ...(hub.partners || []).map(p => ({
        key: keyOf('partner', p.partnerId), type: 'partner', id: p.partnerId,
        label: `Partner: ${p.partnerName}`, name: p.partnerName, n: norm(p.partnerName),
        email: normEmail(p.billingEmail), where: where(p), billingRecord: !!p.billingEmail,
    })),
    ...(hub.accounts || []).map(a => ({
        key: keyOf('account', a.accountId), type: 'account', id: a.accountId,
        label: `Chain: ${a.accountName}`, name: a.accountName, n: norm(a.accountName),
        email: normEmail(a.billingEmail), where: where(a), billingRecord: !!a.billingRecord,
    })),
    ...(hub.hotels || []).map(h => ({
        key: keyOf('hotel', h.hotelId), type: 'hotel', id: h.hotelId,
        label: `Hotel: ${h.hotelName} — ${h.accountName}`, name: h.hotelName, code: h.hotelCode, chain: h.accountName,
        n: norm(h.hotelName), email: normEmail(h.billingEmail), where: where(h), billingRecord: !!h.billingRecord,
    })),
    ...(hub.vendors || []).map(v => ({
        key: keyOf('vendor', v.credentialId), type: 'vendor', id: v.credentialId,
        label: `Vendor: ${v.vendorName}${v.partnerName ? ` — ${v.partnerName}` : ''}`, name: v.vendorName, chain: v.partnerName,
        n: norm(v.vendorName), email: normEmail(v.billingEmail), where: where(v), billingRecord: true,
    })),
];

/** customerId -> 'partner:5' | 'account:12' | 'hotel:34' | 'vendor:7' from the saved links — one per customer. */
export const savedLinkMap = (hub) => new Map((hub.links || []).map(l => [String(l.qboCustomerId),
    l.partnerId ? keyOf('partner', l.partnerId)
        : l.accountId ? keyOf('account', l.accountId)
            : l.hotelId ? keyOf('hotel', l.hotelId) : keyOf('vendor', l.credentialId)]));

/** 'hotel:34' -> the undecided "create a QuickBooks customer" proposal for that record, if the agent has one. */
export const proposalMap = (hub) => new Map((hub.proposals || []).map(p => [keyOf(p.entityType, p.entityId), p.actionId]));

// How well a customer and a record match: the same billing email 4, an exact (normalised) name 3, containment 2.
const matchScore = (customer, target) => {
    const email = normEmail(customer.email);
    if (email && target.email && email === target.email) return 4;
    const names = [customer.customer, customer.company].map(norm).filter(n => n.length >= 4);
    if (!names.length || !target.n || target.n.length < 4) return 0;
    if (names.includes(target.n)) return 3;
    if (names.some(n => (n.length >= 5 && target.n.includes(n)) || (target.n.length >= 5 && n.includes(target.n)))) return 2;
    return 0;
};

/**
 * Up to `limit` targets for a customer, best first; partners, then chains, hotels and vendors at the same score.
 * Each result carries its `score` so a caller can treat exact matches (3+) apart.
 */
export const suggestionsFor = (customer, targets, limit = 3) => targets
    .map(t => ({ ...t, score: matchScore(customer, t) })).filter(t => t.score)
    .sort((a, b) => b.score - a.score || TYPE_RANK[a.type] - TYPE_RANK[b.type])
    .slice(0, limit);

// A suggestion this good is an exact match: the same billing email (4) or an exact name (3).
export const EXACT_SCORE = 3;
export const isExact = (s) => !!s && s.score >= EXACT_SCORE;

/**
 * The ONE target a customer can be linked to by default, or null. Only the best-scoring suggestion, only when it
 * is exact, and only when nothing else scores the same — two equally good matches are a decision, not a default.
 * So an email match beats a different record's exact name, and the count of "exact" matches and the accept
 * action agree because both use this.
 */
export const exactMatchOf = (suggestions = []) => {
    const best = suggestions[0];
    if (!isExact(best)) return null;
    return suggestions.filter(s => s.score === best.score).length === 1 ? best : null;
};

/** The other direction: up to `limit` customers for a billing record, best first. */
export const customersFor = (target, customers, limit = 3) => customers
    .map(c => ({ ...c, score: matchScore(c, target) })).filter(c => c.score)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit);

/** Free-text search over the targets (name, hotel code, chain / partner, billing email), for the "Choose…" pickers. */
export const searchTargets = (targets, text, limit = 15) => {
    const needle = String(text || '').trim().toLowerCase();
    if (!needle) return [];
    return targets.filter(t => [t.name, t.code, t.chain, t.email].some(v => String(v || '').toLowerCase().includes(needle))).slice(0, limit);
};

/** Free-text search over QuickBooks customers (name, company, email, address), for the record → customer picker. */
export const searchCustomers = (customers, text, limit = 15) => {
    const needle = String(text || '').trim().toLowerCase();
    if (!needle) return [];
    return customers.filter(c => [c.customer, c.company, c.email, c.address].some(v => String(v || '').toLowerCase().includes(needle))).slice(0, limit);
};

/** 'partner:5' / 'account:12' / 'hotel:34' / 'vendor:7' -> { entityType, entityId }; '' -> nulls (remove the link). */
export const toSaveLink = (qboCustomerId, key) => {
    const [entityType, entityId] = key ? key.split(':') : [null, null];
    return { qboCustomerId, entityType: entityType || null, entityId: entityId ? parseInt(entityId, 10) : null };
};
